import { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useTranslations } from 'next-intl';
import {
    ArrowsRightLeftIcon,
    ChevronLeftIcon,
    ChevronRightIcon,
} from '@heroicons/react/24/outline';

export function PartnersCarouselSection() {
    const t = useTranslations('partnersCarousel');
    const [activeIndex, setActiveIndex] = useState(0);

    // moved inside component to access translations
    const partners = [
        {
            id: 'inweb',
            name: 'Inweb',
            role: t('partners.inweb.role'),
            tagline: t('partners.inweb.tagline'),
            url: 'https://inweb.ua/ua/?utm_source=partner&utm_medium=afivio',
            logo: '/inweb.png',
            logoAlt: t('partners.inweb.logoAlt'),
            pill: t('partners.inweb.pill'),
            ctaLabel: t('partners.inweb.ctaLabel'),
            paragraphs: [
                t('partners.inweb.paragraphs.0'),
                t('partners.inweb.paragraphs.1'),
                t('partners.inweb.paragraphs.2'),
            ],
            focus: [
                t('partners.inweb.focus.0'),
                t('partners.inweb.focus.1'),
                t('partners.inweb.focus.2'),
            ],
        },
        {
            id: 'second-partner',
            name: t('partners.become.name'),
            role: t('partners.become.role'),
            url: '/contact',
            logo: '/afivio-partner.png',
            logoAlt: t('partners.become.logoAlt'),
            pill: t('partners.become.pill'),
            ctaLabel: t('partners.become.ctaLabel'),
            paragraphs: [
                t('partners.become.paragraphs.0'),
                t('partners.become.paragraphs.1'),
                t('partners.become.paragraphs.2'),
            ],
            focus: [
                t('partners.become.focus.0'),
                t('partners.become.focus.1'),
                t('partners.become.focus.2'),
            ],
        },
    ];

    const currentPartner = partners[activeIndex];
    const hasMultiple = partners.length > 1;

    const handlePrev = () => {
        setActiveIndex((prev) => (prev - 1 + partners.length) % partners.length);
    };

    const handleNext = () => {
        setActiveIndex((prev) => (prev + 1) % partners.length);
    };

    return (
        <section className="py-24 bg-gradient-to-b from-gray-50 to-white text-slate-900">
            <div className="max-w-6xl mx-auto px-6">
                <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-6 mb-10">
                    <div>
                        <p className="text-xs uppercase tracking-[0.18em] text-slate-400 mb-2">
                            {t('header.kicker')}
                        </p>
                        <h2 className="text-3xl md:text-4xl font-bold mb-2">
                            {t('header.title')}
                        </h2>
                        <p className="text-sm md:text-base text-slate-600 max-w-xl">
                            {t('header.subtitle')}
                        </p>
                    </div>

                    {hasMultiple && (
                        <div className="flex items-center gap-3 self-end md:self-auto">
                            <button
                                type="button"
                                onClick={handlePrev}
                                className="w-9 h-9 rounded-full border border-slate-300 flex items-center justify-center hover:bg-slate-100 transition"
                                aria-label={t('navigation.previous')}
                            >
                                <ChevronLeftIcon className="w-4 h-4" />
                            </button>
                            <span className="text-xs uppercase tracking-wide text-slate-400">
                {activeIndex + 1} / {partners.length}
              </span>
                            <button
                                type="button"
                                onClick={handleNext}
                                className="w-9 h-9 rounded-full border border-slate-300 flex items-center justify-center hover:bg-slate-100 transition"
                                aria-label={t('navigation.next')}
                            >
                                <ChevronRightIcon className="w-4 h-4" />
                            </button>
                        </div>
                    )}
                </div>

                <div className="grid md:grid-cols-[1.25fr,0.9fr] gap-10 items-stretch">
                    <AnimatePresence mode="wait">
                        <motion.div
                            key={currentPartner.id + '-text'}
                            initial={{ opacity: 0, x: -24 }}
                            animate={{ opacity: 1, x: 0 }}
                            exit={{ opacity: 0, x: 24 }}
                            transition={{ duration: 0.25 }}
                            className="bg-white border border-slate-200 rounded-3xl p-8 flex flex-col justify-between shadow-sm"
                        >
                            <div>
                                <div className="flex items-center gap-3 mb-6">
                  <span className="inline-flex items-center rounded-full border border-slate-200 px-3 py-1 text-[10px] uppercase tracking-[0.18em] text-slate-500 bg-slate-50">
                    {t('badges.partner')}
                  </span>
                                    {currentPartner.pill && (
                                        <span className="inline-flex items-center rounded-full bg-emerald-50 text-emerald-700 border border-emerald-100 px-3 py-1 text-[10px] uppercase tracking-[0.16em]">
                      {currentPartner.pill}
                    </span>
                                    )}
                                </div>

                                <h3 className="text-2xl md:text-3xl font-semibold mb-3">
                                    {currentPartner.name}
                                </h3>

                                <div className="relative mt-2">
                                    <div className="absolute -left-4 top-2 bottom-2 w-1 rounded-full bg-gradient-to-b from-blue-500 to-emerald-400 opacity-70" />
                                    <motion.div
                                        whileHover={{ y: -2 }}
                                        transition={{ duration: 0.18 }}
                                        className="pl-4 rounded-xl bg-gradient-to-r from-blue-50/60 via-white to-white border border-slate-100 shadow-[0_8px_20px_rgba(15,23,42,0.03)]"
                                    >
                                        <div className="p-4 space-y-3 text-sm text-slate-700 leading-relaxed">
                                            {currentPartner.paragraphs.map((p, idx) => (
                                                <p key={idx}>{p}</p>
                                            ))}
                                        </div>
                                    </motion.div>
                                </div>
                            </div>

                            <div className="pt-6 flex flex-wrap items-center gap-4">
                                <a
                                    href={currentPartner.url}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="inline-flex items-center rounded-xl bg-slate-900 text-white px-5 py-2.5 text-sm font-medium hover:bg-slate-800 transition shadow-sm"
                                >
                                    {currentPartner.ctaLabel}
                                    <ArrowsRightLeftIcon className="w-4 h-4 ml-2" />
                                </a>
                            </div>
                        </motion.div>

                        <motion.div
                            key={currentPartner.id + '-side'}
                            initial={{ opacity: 0, x: 24 }}
                            animate={{ opacity: 1, x: 0 }}
                            exit={{ opacity: 0, x: -24 }}
                            transition={{ duration: 0.25 }}
                            className="bg-gray-50 border border-slate-200 rounded-3xl p-8 flex flex-col justify-between"
                        >
                            <div>
                                <div className="mb-6">
                                    <div className="w-full rounded-3xl bg-white border border-slate-100 shadow-sm overflow-hidden flex items-center justify-center">
                                        {currentPartner.logo && (
                                            <img
                                                src={currentPartner.logo}
                                                alt={currentPartner.logoAlt}
                                                className="w-full max-h-40 object-contain"
                                            />
                                        )}
                                    </div>

                                    <div className="mt-4">
                                        <p className="text-[11px] uppercase tracking-[0.16em] text-slate-400 mb-1">
                                            {t('sidebar.keyCapabilities')}
                                        </p>
                                        <p className="text-sm text-slate-700">{currentPartner.role}</p>
                                    </div>
                                </div>

                                <ul className="space-y-2 text-sm text-slate-700">
                                    {currentPartner.focus?.map((item, idx) => (
                                        <li key={idx} className="flex gap-2">
                                            <span className="mt-[7px] h-1.5 w-1.5 rounded-full bg-emerald-500" />
                                            <span>{item}</span>
                                        </li>
                                    ))}
                                </ul>
                            </div>

                            {hasMultiple && (
                                <div className="pt-6 flex items-center justify-between border-t border-slate-200 mt-6">
                  <span className="text-xs text-slate-400">
                    {t('navigation.browsing', {
                        current: activeIndex + 1,
                        total: partners.length,
                    })}
                  </span>
                                    <div className="flex gap-2">
                                        <button
                                            type="button"
                                            onClick={handlePrev}
                                            className="w-8 h-8 rounded-full border border-slate-300 flex items-center justify-center hover:bg-slate-100 transition"
                                            aria-label={t('navigation.previous')}
                                        >
                                            <ChevronLeftIcon className="w-4 h-4" />
                                        </button>
                                        <button
                                            type="button"
                                            onClick={handleNext}
                                            className="w-8 h-8 rounded-full border border-slate-300 flex items-center justify-center hover:bg-slate-100 transition"
                                            aria-label={t('navigation.next')}
                                        >
                                            <ChevronRightIcon className="w-4 h-4" />
                                        </button>
                                    </div>
                                </div>
                            )}
                        </motion.div>
                    </AnimatePresence>
                </div>
            </div>
        </section>
    );
}
